import React, { useState, useEffect } from 'react';
import { rememberUpsellToken } from '../../../utils/upsellToken';
import { SelectedPurchaseType } from '../../../types/types';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel
} from '@mui/material';
import { useUser } from '../../../contexts/UserContext';
import {
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { useNetwork } from '../../../utils/network';
import { useShowSnackbar } from '../../../utils/snackbar';
import { track, trackError } from '../../../utils/track';
import {
  getFirstMonthDiscountedPrice,
  isDiscountEligibleSubscriptionPackage
} from '../../../utils/subscriptionDiscounts';
import { promoHeadline, rewardLines, rewardParts } from '../../PromoPack/promoPack';
import { PromoRewardTiles } from '../../PromoPack/PromoPackPage';

type PurchaseModalProps = {
  handlePurchaseModalClose: () => void;
  selectedPurchase: SelectedPurchaseType | null;
  handleBonusPurchaseSuccess: () => void;
  setPaymentIntentIdAfterPayment: (value: string) => void;
};

export const PurchaseContent = ({
  handlePurchaseModalClose,
  selectedPurchase,
  handleBonusPurchaseSuccess,
  setPaymentIntentIdAfterPayment
}: PurchaseModalProps) => {
  const { user } = useUser();
  const { get, post } = useNetwork();
  const { showSnackbar } = useShowSnackbar();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState<boolean>(false);
  const [stripeLoading, setStripeLoading] = useState<boolean>(false);
  const [acceptTnC, setAcceptTnC] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<any[]>([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<string>('new');

  const getDiscountedSubscriptionPrice = (
    purchase: SelectedPurchaseType | null
  ) => {
    if (!purchase) return 0;

    const isSubscription = purchase.type === 'Subscription';
    const isDiscountEligible = isDiscountEligibleSubscriptionPackage(
      purchase.id
    );

    if (isSubscription && isDiscountEligible) {
      return getFirstMonthDiscountedPrice(purchase.price);
    }

    return Number(purchase.price);
  };

  const getMemberPortalSubscriptionBonusEntries = (
    purchase: SelectedPurchaseType | null
  ) => {
    if (purchase?.type !== 'Subscription') return 0;
    return Math.max(0, Math.floor(Number(purchase.entries || 0) * 10));
  };

  const getMemberPortalSubscriptionBonusPayload = (
    purchase: SelectedPurchaseType | null
  ) => {
    const bonusEntries = getMemberPortalSubscriptionBonusEntries(purchase);

    if (bonusEntries <= 0) return {};

    return {
      signupCodeBonusEntries: bonusEntries,
      signupCodeBonusSource: 'member_portal_subscription'
    };
  };

  const [fiatAmount, setFiatAmount] = useState<number>(
    getDiscountedSubscriptionPrice(selectedPurchase)
  );
  const [bonus, setBonus] = useState<boolean>(false);
  const memberCheckoutDetails = {
    email: user.email,
    username: user.username ?? '',
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    country: user.country ?? '',
    isGiveawayEligible: Boolean(user.isGiveawayEligible),
    phone: user.phone ?? '',
    state: user.state ?? '',
    referralCode: ''
  };

  useEffect(() => {
    if (selectedPurchase) {
      setFiatAmount(getDiscountedSubscriptionPrice(selectedPurchase));
    }
  }, [selectedPurchase]);

  // The left (product) column is sized to the artwork exactly, so the image
  // fills it with no leftover gutter — that keeps the middle gap equal to the
  // outer padding instead of being padded out by dead space beside the image.
  const artWidth = selectedPurchase?.type === 'Promotion' ? 340 : 260;
  // Promotion art is landscape (a wide "limited offer" banner); every other
  // pack is portrait. Promotion gets a full-width banner on top instead of the
  // narrow portrait column, plus the upgrade upsell card.
  const isPromotion = selectedPurchase?.type === 'Promotion';
  // This week's headline and reward lines, read the way the public /promo
  // page reads them, so both print the same lines. No lines until the admin
  // announces them — there is no default.
  const promoHeadlineText = isPromotion
    ? promoHeadline(selectedPurchase?.rewards)
    : '';
  // the same parts the public page draws as tiles
  const promoParts = rewardParts(isPromotion ? selectedPurchase?.rewards : undefined);
  const promoRewardLines = isPromotion
    ? rewardLines(selectedPurchase?.rewards)
    : [];

  const resetState = () => {
    setLoading(false);
    setStripeLoading(false);
    setAcceptTnC(false);
    setMessage('');
    setSavedPaymentMethods([]);
    setSelectedPaymentMethod('new');
    setFiatAmount(0);
    setBonus(false);
    handlePurchaseModalClose();
  };

  useEffect(() => {
    const fetchSavedPaymentMethods = async () => {
      setLoading(true);
      try {
        const data = await get('/user/payment-methods', true);
        setSavedPaymentMethods(data.paymentMethods);
      } catch (error) {
        console.error('Error fetching saved payment methods:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSavedPaymentMethods();
  }, []);

  const getCurrentSubscriptionAndCloseModal = async () => {
    try {
      await get('user/subscription', true);
      handlePurchaseModalClose();

      // A pack/promo purchase drops an unclaimed pack the user opens on the
      // dashboard, and that reveal plays for several seconds. The hard page
      // reload here (originally just to surface the new pack) was firing
      // MID-REVEAL and wiping it. For packs, refresh the unclaimed list in
      // place via an event instead — only a subscription purchase still does
      // the full reload, to reflect the new plan across the app.
      const isPack =
        selectedPurchase?.type === 'Standalone' ||
        selectedPurchase?.type === 'Promotion';
      if (isPack) {
        window.dispatchEvent(new Event('incrypt:packs-updated'));
      } else {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      }
    } catch (error: any) {
      console.log(error);
    }
  };

  const handleToggle = () => {
    setBonus((prev) => {
      const updatedBonus = !prev;
      const priceAdjustment = selectedPurchase?.Bonus?.price || 0;

      if (updatedBonus) {
        setFiatAmount((prevPrice) => prevPrice + Number(priceAdjustment)); // Add price if opted in
      } else {
        setFiatAmount((prevPrice) => prevPrice - Number(priceAdjustment)); // Subtract price if opted out
      }

      return updatedBonus;
    });
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) {
      setMessage(
        'Stripe has not loaded. Please refresh the page and try again.'
      );
      return;
    }

    setStripeLoading(true);
    setMessage('');

    const type =
      selectedPurchase?.type === 'Standalone' ||
      selectedPurchase?.type === 'Promotion'
        ? 'standalone'
        : 'subscription';
    const usingPaymentElement = selectedPaymentMethod === 'new';
    const returnUrl = window.location.href;

    // Payment friction: fires alongside each Stripe error path below. Purely
    // additive — never changes the existing snackbar/return flow — and feeds the
    // CRM's "why behind drop-off" (trackError defaults its category to 'friction').
    const trackPaymentFail = (stage: string, err: any) =>
      trackError('purchase.payment', err, {
        stage,
        packageId: selectedPurchase?.id,
        packageType: selectedPurchase?.type,
        kind: type,
        method: usingPaymentElement ? 'new_card' : 'saved_card',
        code: err?.code,
        declineCode: err?.decline_code
      });

    try {
      // If using a NEW card (Payment Element), submit it first.
      if (usingPaymentElement) {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          trackPaymentFail('card_details', submitError);
          setMessage(
            submitError.message || 'Failed to submit payment details.'
          );
          setStripeLoading(false);
          return;
        }
      }

      if (type === 'subscription' && usingPaymentElement) {
        // --- Subscription + NEW card ---
        // 1) Precheck SI (trial_precheck) to get a PM
        const si = await post(
          '/checkout/create-trial-setup-intent',
          {
            ...memberCheckoutDetails
          },
          true
        );

        const { error: setupErr, setupIntent } = await stripe.confirmSetup({
          elements,
          clientSecret: si.clientSecret,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required'
        });
        if (setupErr) {
          trackPaymentFail('subscription_setup', setupErr);
          showSnackbar(setupErr.message || 'Card setup failed.', 'error');
          setStripeLoading(false);
          return;
        }

        const pmId = setupIntent.payment_method as string;

        // 2) Create subscription using that PM
        const resData = await post(
          '/checkout/create-subscription-payment-intent',
          {
            packageId: selectedPurchase?.id || '',
            hasBonus: false,
            fiatAmount,
            ...memberCheckoutDetails,
            ...getMemberPortalSubscriptionBonusPayload(selectedPurchase),
            paymentMethodId: pmId
          },
          true
        );

        const secret = resData?.clientSecret ?? null;

        // if (!secret) {
        //   // Trial created; nothing to confirm now.
        //   showSnackbar('Your free trial has started!', 'success');
        //   await getCurrentSubscriptionAndCloseModal();
        //   return;
        // }

        /* A null clientSecret used to reach .startsWith and throw a raw
         * TypeError instead of telling the member anything useful. */
        if (!secret || typeof secret !== 'string') {
          showSnackbar(
            'We could not start this payment. Please try again.',
            'error'
          );
          setStripeLoading(false);
          return;
        }

        if (secret.startsWith('pi_')) {
          await elements.submit();
          const { error } = await stripe.confirmPayment({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });
          if (error) {
            trackPaymentFail('subscription_payment', error);
            showSnackbar(
              error.message || 'Payment confirmation failed',
              'error'
            );
            return;
          }
        } else if (secret.startsWith('seti_')) {
          await elements.submit();
          const { error } = await stripe.confirmSetup({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });
          if (error) {
            trackPaymentFail('subscription_setup_confirm', error);
            showSnackbar(error.message || 'Card setup failed', 'error');
            return;
          }
        }

        track('purchase.success', {
          packageId: selectedPurchase?.id,
          kind: selectedPurchase?.type,
          flow: 'subscription'
        });
        showSnackbar('Stripe is processing your payment…', 'success');
        await getCurrentSubscriptionAndCloseModal();
        return;
      }

      const attemptId = crypto.randomUUID();

      // --- Everything else (subscription + saved, standalone + new/saved) ---
      const resData = await post(
        `/checkout/create-${type}-payment-intent`,
        {
          packageId: selectedPurchase?.id || '',
          hasBonus: bonus,
          fiatAmount,
          ...memberCheckoutDetails,
          ...(type === 'subscription'
            ? getMemberPortalSubscriptionBonusPayload(selectedPurchase)
            : {}),
          paymentMethodId: usingPaymentElement ? null : selectedPaymentMethod,
          attemptId
        },
        true
      );

      const secret = resData?.clientSecret ?? null;

      // Handle trial (server returned null secret)
      // if (!secret) {
      //   showSnackbar('Your free trial has started!', 'success');
      //   await getCurrentSubscriptionAndCloseModal();
      //   return;
      // }

      if (!secret || typeof secret !== 'string') {
        showSnackbar(
          'We could not start this payment. Please try again.',
          'error'
        );
        setStripeLoading(false);
        return;
      }

      if (secret.startsWith('pi_')) {
        if (usingPaymentElement) {
          // Standalone + NEW card
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });
          if (error) {
            trackPaymentFail('standalone_payment', error);
            showSnackbar(
              error.message || 'An unexpected error occurred.',
              'error'
            );
            return;
          }
          if (type === 'standalone') {
            if (![10, 31, 32, 41, 42].includes(selectedPurchase?.id ?? -1)) {
              if (paymentIntent?.id) {
                if (selectedPurchase?.type === 'Promotion' && !bonus) {
                  handlePurchaseModalClose();
                } else {
                  // keep the token that authorises the upsell charge for this purchase
                  const canUpsell = rememberUpsellToken(
                    paymentIntent.id,
                    resData?.upsellToken
                  );
                  setPaymentIntentIdAfterPayment(paymentIntent.id);
                  // Without it the offer cannot be charged, so it is not shown at
                  // all — the purchase already succeeded (see utils/upsellToken).
                  if (canUpsell) {
                    handleBonusPurchaseSuccess();
                    return;
                  }
                }
              }
              handlePurchaseModalClose();
            } else {
              await getCurrentSubscriptionAndCloseModal();
            }
          }
        } else {
          // Saved card (no elements)
          const { error, paymentIntent } = await stripe.confirmCardPayment(
            secret,
            {
              // You can omit payment_method if the PI already has it; keeping it is fine for saved card
              payment_method: selectedPaymentMethod,
              return_url: returnUrl
            }
          );
          if (error) {
            trackPaymentFail('standalone_payment_saved', error);
            showSnackbar(
              error.message || 'An unexpected error occurred.',
              'error'
            );
            return;
          }

          // Your bonus logic for standalone (saved)
          if (type === 'standalone') {
            if (![10, 31, 32, 41, 42].includes(selectedPurchase?.id ?? -1)) {
              if (paymentIntent?.id) {
                if (selectedPurchase?.type === 'Promotion' && !bonus) {
                  handlePurchaseModalClose();
                } else {
                  // keep the token that authorises the upsell charge for this purchase
                  const canUpsell = rememberUpsellToken(
                    paymentIntent.id,
                    resData?.upsellToken
                  );
                  setPaymentIntentIdAfterPayment(paymentIntent.id);
                  // Without it the offer cannot be charged, so it is not shown at
                  // all — the purchase already succeeded (see utils/upsellToken).
                  if (canUpsell) {
                    handleBonusPurchaseSuccess();
                    return;
                  }
                }
              }
              handlePurchaseModalClose();
            }
          } else {
            await getCurrentSubscriptionAndCloseModal();
          }
        }
      } else if (secret.startsWith('seti_')) {
        // Rare path for saved-card setup; confirm defensively
        if (usingPaymentElement) {
          const { error } = await stripe.confirmSetup({
            elements,
            clientSecret: secret,
            confirmParams: { return_url: returnUrl },
            redirect: 'if_required'
          });
          if (error) {
            trackPaymentFail('card_setup', error);
            showSnackbar(error.message || 'Card setup failed.', 'error');
            return;
          }
        } else {
          const { error } = await stripe.confirmCardSetup(secret, {
            return_url: returnUrl
          });
          if (error) {
            trackPaymentFail('card_setup_saved', error);
            showSnackbar(error.message || 'Card setup failed.', 'error');
            return;
          }
        }
      }

      track('purchase.success', {
        packageId: selectedPurchase?.id,
        kind: selectedPurchase?.type,
        flow: type
      });
      showSnackbar(
        type === 'standalone'
          ? 'Stripe is processing your payment! Please head to the Dashboard page to claim your pack.'
          : 'Stripe is processing your subscription…',
        'success'
      );
    } catch (error) {
      trackPaymentFail('exception', error);
      showSnackbar(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred.',
        'error'
      );
    } finally {
      setStripeLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {loading ? (
        <CircularProgress />
      ) : (
        !!selectedPurchase && (
          <form onSubmit={handleFormSubmit}>
            {/* Promotion: landscape banner across the top (portrait packs keep
                their image in the left column below). */}
            {isPromotion && (
              <Box sx={{ mb: { xs: 2, md: 2.5 } }}>
                <Box
                  component="img"
                  src={selectedPurchase.imageUrl}
                  alt=""
                  sx={{
                    display: 'block',
                    mx: 'auto',
                    width: '100%',
                    maxWidth: 760,
                    height: 'auto',
                    maxHeight: { xs: 200, md: 280 },
                    objectFit: 'contain',
                    borderRadius: '14px'
                  }}
                />
                {/* Under the banner: the headline, then this week's rewards —
                    one line each, or "Rewards to be announced" until the admin
                    sets them. What the week's draw offers, not what this
                    purchase grants. Admin-written — React text only. */}
                <Box sx={{ mt: { xs: 2.5, md: 3.5 }, textAlign: 'center' }}>
                  {promoHeadlineText && (
                    <Typography
                      sx={{
                        fontWeight: 900,
                        fontSize: 18,
                        color: '#e8f4fb',
                        overflowWrap: 'anywhere'
                      }}
                    >
                      {promoHeadlineText}
                    </Typography>
                  )}
                  <Typography
                    sx={{
                      mt: promoHeadlineText ? 1.5 : 0,
                      font: '800 13px ui-monospace, Menlo, Consolas, monospace',
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      color: '#38e8ff'
                    }}
                  >
                    Up for grabs in this week&apos;s promo draw
                  </Typography>
                  {promoRewardLines.length > 0 ? (
                    /* the public page's reward tiles — the coin, the shining
                       figure, the pack with its art and count, the custom line */
                    <Box sx={{ mt: 1.5, maxWidth: 640, mx: 'auto', textAlign: 'left' }}>
                      <PromoRewardTiles parts={promoParts} large />
                    </Box>
                  ) : (
                    <Typography
                      sx={{
                        mt: 0.75,
                        fontSize: 14,
                        fontWeight: 700,
                        color: '#8ea3b3'
                      }}
                    >
                      Rewards to be announced
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            <Box
              sx={{
                display: { xs: 'block', md: 'flex' },
                justifyContent: { md: 'space-evenly' },
                alignItems: { md: 'flex-start' },
                // Bleed past the modal's 24px side padding so the two columns
                // span the full width; then space-evenly makes the three gaps
                // (left of image · between · right of form) all equal — the
                // outer gaps take the place of the padding cancelled here.
                width: { md: 'calc(100% + 48px)' },
                mx: { md: '-24px' }
              }}
            >
              {/* ===== left: what you're buying ===== */}
              <Box
                sx={{
                  flex: { md: '0 0 auto' },
                  width: { md: `${artWidth}px` },
                  minWidth: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center'
                }}
              >
            {/* Product image — portrait packs only; Promotion uses the top banner */}
            {!isPromotion && (
              <Box sx={{ textAlign: 'center', pt: 0.5 }}>
                <Box
                  component="img"
                  src={selectedPurchase.imageUrl}
                  alt=""
                  sx={{
                    display: 'block',
                    mx: 'auto',
                    width: '100%',
                    maxWidth: artWidth,
                    height: 'auto',
                    objectFit: 'contain',
                    borderRadius: '10px'
                  }}
                />
              </Box>
            )}

            {selectedPurchase.Bonus && isPromotion && (
              <Box
                role="button"
                tabIndex={0}
                aria-pressed={bonus}
                onClick={() => {
                  if (!stripeLoading) handleToggle();
                }}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !stripeLoading) {
                    e.preventDefault();
                    handleToggle();
                  }
                }}
                sx={{
                  width: '100%',
                  mt: { xs: 2, md: 0.5 },
                  mb: 1,
                  p: 2.25,
                  cursor: stripeLoading ? 'default' : 'pointer',
                  borderRadius: '14px',
                  border: bonus
                    ? '1px solid rgba(255, 207, 64, 0.75)'
                    : '1px solid rgba(255, 207, 64, 0.28)',
                  background: bonus
                    ? 'linear-gradient(180deg, rgba(255, 207, 64, 0.16), rgba(255, 207, 64, 0.04))'
                    : 'linear-gradient(180deg, rgba(255, 207, 64, 0.06), rgba(11, 17, 25, 0.35))',
                  boxShadow: bonus
                    ? '0 0 24px -6px rgba(255, 207, 64, 0.45)'
                    : 'none',
                  transition:
                    'border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease',
                  '&:focus-visible': {
                    outline: '2px solid rgba(255, 207, 64, 0.7)',
                    outlineOffset: '2px'
                  }
                }}
              >
                <Typography
                  sx={{
                    font: '800 10px ui-monospace, Menlo, Consolas, monospace',
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: '#ffcf40',
                    textAlign: 'center'
                  }}
                >
                  ⚡ Limited upgrade
                </Typography>
                <Typography
                  sx={{
                    mt: 0.75,
                    textAlign: 'center',
                    fontWeight: 900,
                    fontSize: 17,
                    color: '#e8f4fb'
                  }}
                >
                  Get more rewards
                </Typography>

                <Box
                  sx={{
                    mt: 2,
                    display: 'flex',
                    alignItems: 'stretch',
                    justifyContent: 'center',
                    gap: 2
                  }}
                >
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography
                      sx={{
                        font: '900 26px ui-monospace, Menlo, Consolas, monospace',
                        lineHeight: 1,
                        color: '#38e8ff'
                      }}
                    >
                      +{selectedPurchase.Bonus.extraCards}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.75,
                        font: '800 9px ui-monospace, Menlo, Consolas, monospace',
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: '#8ea3b3'
                      }}
                    >
                      Extra cards
                    </Typography>
                  </Box>
                  <Box
                    sx={{ width: '1px', background: 'rgba(140, 170, 190, 0.18)' }}
                  />
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <Typography
                      sx={{
                        font: '900 26px ui-monospace, Menlo, Consolas, monospace',
                        lineHeight: 1,
                        color: '#38e8ff'
                      }}
                    >
                      +{selectedPurchase.Bonus.extraEntries}
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.75,
                        font: '800 9px ui-monospace, Menlo, Consolas, monospace',
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: '#8ea3b3'
                      }}
                    >
                      Extra entries
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    mt: 2,
                    pt: 1.75,
                    borderTop: '1px solid rgba(140, 170, 190, 0.16)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Checkbox
                      checked={bonus}
                      onChange={handleToggle}
                      onClick={(e) => e.stopPropagation()}
                      disabled={!!stripeLoading}
                      sx={{
                        p: 0.5,
                        color: 'rgba(255, 207, 64, 0.55)',
                        '&.Mui-checked': { color: '#ffcf40' }
                      }}
                    />
                    <Typography
                      sx={{ fontWeight: 800, fontSize: 14, color: '#e8f4fb' }}
                    >
                      Add upgrade
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      font: '900 16px ui-monospace, Menlo, Consolas, monospace',
                      color: '#ffcf40'
                    }}
                  >
                    +A${Number(selectedPurchase.Bonus.price || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            )}

            {selectedPurchase?.type === 'Subscription' &&
              isDiscountEligibleSubscriptionPackage(selectedPurchase.id) && (
                <Box
                  sx={{
                    my: 2,
                    p: 2,
                    borderRadius: '10px',
                    border: '1px solid rgba(56, 232, 255, 0.35)',
                    background: 'rgba(56, 232, 255, 0.10)',
                    textAlign: 'center'
                  }}
                >
                  <Typography sx={{ fontWeight: 900, color: 'white' }}>
                    30% off your first month
                  </Typography>
                  <Typography
                    sx={{ mt: 0.5, color: 'text.secondary', fontWeight: 700 }}
                  >
                    After the first month, your subscription renews at the
                    normal monthly price.
                  </Typography>
                </Box>
              )}

            {/* Token & Fiat Section */}
            <Box
              sx={{
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <Typography
                variant="h4"
                sx={{ mt: 2, fontWeight: 'bold', textAlign: 'center' }}
              >
                Fiat Amount: ${fiatAmount.toFixed(2)}
              </Typography>
            </Box>
              </Box>

              {/* ===== right: payment ===== */}
              <Box sx={{ flex: { md: '0 1 440px' }, minWidth: 0 }}>
            {/* Payment Method Section */}
            {fiatAmount > 0 && (
              <Box
                sx={{
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Box
                  sx={{
                    width: '100%'
                  }}
                >
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Payment Method</FormLabel>
                    <RadioGroup
                      value={selectedPaymentMethod}
                      onChange={(e) => setSelectedPaymentMethod(e.target.value)}
                    >
                      {savedPaymentMethods.map((method) => (
                        <FormControlLabel
                          key={method.id}
                          value={method.id}
                          control={<Radio />}
                          label={`Saved card ending in ${method.card.last4}`}
                        />
                      ))}
                      <FormControlLabel
                        value="new"
                        control={<Radio />}
                        label="New Card"
                      />
                    </RadioGroup>
                  </FormControl>
                  {selectedPaymentMethod === 'new' && (
                    <Box sx={{ minHeight: '370px' }}>
                      <PaymentElement
                        options={{ paymentMethodOrder: ['card'] }}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Terms & Conditions */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptTnC}
                    onChange={(e) => setAcceptTnC(e.target.checked)}
                    required
                  />
                }
                sx={{ width: '100%' }}
                label="I accept the terms and conditions."
              />
            </Box>

            {/* Error Message */}
            {message && (
              <Typography
                variant="body2"
                color="error"
                sx={{
                  marginTop: 2,
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
              >
                {message}
              </Typography>
            )}

            {/* Submit Button */}
            <Box
              sx={{
                marginTop: '24px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!stripe || stripeLoading}
                >
                  {stripeLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Submit Payment'
                  )}
                </Button>
                <Button
                  variant="contained"
                  fullWidth
                  color="error"
                  sx={{ marginTop: '20px' }}
                  onClick={() => handlePurchaseModalClose()}
                  disabled={!!stripeLoading}
                >
                  Cancel
                </Button>
              </Box>
            </Box>
              </Box>
            </Box>
          </form>
        )
      )}
    </Box>
  );
};
